import { CommonColumnProperties, ImportWizard, ImportWizardRef, ImportWizardSetting, Title } from "components";
import { ProcessingState, ValidationState } from "components/ImportWizard/ImportWizardTypes";
import { runBatchWorkers } from "components/WebWorkers/ZWebWorker";
import { ZCheckboxButton } from "components/ZCheckboxButton";
import { ZIcon } from "components/ZIcon";
import { ZLabel } from "components/ZLabel";
import { ZMultiSelectDropdown } from "components/ZMultiSelectDropdown";
import { ZTooltip } from "components/ZTooltip";
import { onlyUnique, useDisplayError } from "helpers/Functions";
import { getRowIds } from "helpers/data/dataHandling";
import { LoadFilter, SaveFilter } from "helpers/filterStorage";
import { ZIconType } from "helpers/utility/iconMap";
import { useProductFilter, useStockFilter } from "hooks";
import { SystemStocksPopup } from "popups/SystemStocks/SystemStocks";
import { useCallback, useEffect, useRef, useState } from "react";
import { Card, Col, Dropdown, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getDepartments } from "redux/slices/Departments";
import { importFromProducts } from "redux/slices/Products";
import { createStockGroup, getStockGroups } from "redux/slices/StockGroups";
import {
	addAGGridSelectedRows,
	addAGGrideDeselectedRows,
	cleanStockDetails,
	clearFilters,
	createStock,
	deleteStocks,
	getStockTable,
	getStockTableOptimised,
	importFromProductsWithData,
	updateMultipleStocks,
	updateStockField,
} from "redux/slices/Stocks";
import { getSubDepartments } from "redux/slices/SubDepartment";
import { getSuppliers } from "redux/slices/Suppliers";
import { RootState } from "redux/store";
import { ManagedStockMerge } from "./ManagedStockMerge";
import { BulkAgGridEdit } from "./Popups/BulkEdit/BulkEditAgGrid";
import { ManagedStockSync } from "./Popups/ManagedStockSync";
import { StockDetailImportWorkerData, StockDetailImportWorkerResponseData } from "./stockDetailsImportWorker";
import { SystemStocksImportWorkerData, SystemStocksImportWorkerResponseData } from "./systemStocksImportWorker";
import { ZFeatureFlag } from "components/ZFeatureFlag";
import { AgGridReact } from "ag-grid-react";
import { ManagedStockOptimizedView, ManagedStockView } from "models/dbModels/managedStock/ManagedStock";
import { IServerSideDatasource } from "ag-grid-enterprise";
import { AdvancedFilterModel, ColumnState, FilterModel, GridReadyEvent, IServerSideSelectionState, RowSelectedEvent } from "ag-grid-community";
import { Z } from "models/common/components";
import { AgGridToolbar, AgGridToolbarRef } from "components/AgGrid/AgGridToolbar";
import { FilterState, createBooleanSelectFilter, createSingleSelectFilter } from "components/AgGrid/FilterComponents/Filters";
import { ManagedDepartment } from "models/dbModels/managedDepartments/ManagedDepartment";
import { ManagedSupplier } from "models/dbModels/managedSuppliers/ManagedSupplier";
import { ManagedSubdepartment } from "models/dbModels/managedSubdepartments/ManagedSubdepartment";
import { ManagedStockGroup } from "models/dbModels/managedStockGroups/ManagedStockGroup";
import { ScheduleCodes } from "constants/constants";
import { SearchState } from "components/AgGrid/FilterComponents/Search";
import { ZButton } from "components/ZButton";
import { getManufacturers } from "redux/slices/ManagedManufacturers";
import { getManagedStockViewConfig, getManagedStockCoverPage, getManagerStockDataFormatter, getManagerStockCsvDataFormatter} from "TableConfigs/ManagedStockViewConfig";
import { importWizardExtraMatches } from "TableConfigs/ManagedStockImportWizardConfig";
import { ManagedStockSearchFilterQueryLoadDetails } from "models/filters/managedStocks/ManagedStockSearchFilterQuery";
import BaseCellRenderer from "components/AgGrid/CellRenderers/BaseCellRenderer";
import DateCellRenderer from "components/AgGrid/CellRenderers/DateCellRenderer";
import PercentCellRenderer from "components/AgGrid/CellRenderers/PercentCellRenderer";
import PriceCellRenderer from "components/AgGrid/CellRenderers/PriceCellRenderer";
import {ManagedStocksDeletePopup} from "./Popups/ManagedStocksDeletePopup";
import { ManagedManufacturer } from "models/dbModels/managedManufacturers/ManagedManufacturer";
import { Modal as BSModal } from "react-bootstrap";


export function ManagedStocks() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const getProductFilterOptions = useProductFilter();
	const [syncOpen, setSyncOpen] = useState<boolean>(false);
	const stocks = useAppSelector((state: RootState) => state.stocks);
	const products = useAppSelector((state: RootState) => state.products);
	const departments = useAppSelector((state: RootState) => state.department);
	const subDepartments = useAppSelector((state: RootState) => state.subDepartment);
	const suppliers = useAppSelector((state: RootState) => state.suppliers);
	const stockGroups = useAppSelector((state: RootState) => state.stockGroups);
	const global = useAppSelector((state: RootState) => state.global);
	const manufacturers = useAppSelector((state: RootState) => state.managedManufacturers);
	const [importPercentage, setImportPercentage] = useState<number>(0);
	const [importLabel, setImportLabel] = useState<string | undefined>(undefined);
	const [counterSelectedRows, setCounterSelectedRows] = useState<number>(0);
	const [showSystemStocksPopup, setShowSystemStocksPopup] = useState<boolean>(false);
	const user = useAppSelector((state: RootState) => state.logedinUser);
	const importWizardRef = useRef<ImportWizardRef>(null);
	const [selectedDepartment, setSelectedDepartment] = useState<ManagedDepartment | undefined>(undefined);
	const gridSearch = useRef<{ searchState?: SearchState; filterState?: FilterState, isCsvExport?: boolean, isExcelExport?: boolean }>({});
	const toolbarRef = useRef<AgGridToolbarRef>(null);
	const [serverSideSelectionState, setServerSideSelectionState] = useState<{ selectAll: boolean; toggledNodes: ManagedStockView[] }>({ selectAll: false, toggledNodes: [] });
	const [temp, setTemp] = useState<{ settings: GridSettings }>();
	const [openedFromDropdown, setOpenedFromDropdown] = useState<boolean>(false);
	const [createStockFromNewLines, setCreateStockFromNewLines] = useState<boolean>(openedFromDropdown);
	const [updateStockName, setUpdateStockName] = useState<boolean>(false);
	const [selectedStockGroupIds, setSelectedStockGroupIds] = useState<number[]>([]);
	const terminateWorkersRef = useRef<Function>();
	const batchSize = 5000;
	const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
	const [isDeletingStock, setIsDeletingStock] = useState<boolean>(false);
	const [isExportingCsv, setIsExportingCsv] = useState<boolean>(false);
	const [isExportingExcel, setIsExportingExcel] = useState<boolean>(false);
	const [cacheBlockSize, setCacheBlockSize] = useState<number>(100);
	const [totalRows, setTotalRows] = useState<number>(0);
	const [filterRequiresRefresh, setFilterRequiresRefresh] = useState<boolean>(true);
	type GridSettings = {
		columnState: ColumnState[];
		filterState: FilterModel;
		toolbarFilterState: Map<string, any>;
		advancedFilterState: AdvancedFilterModel | null;
		searchState: SearchState;
		name: string;
		description: string;
	};

	useEffect(() => {		
		dispatch(cleanStockDetails());
		dispatch(clearFilters());
		dispatch(getStockTable({}));
		LoadFilter<{ searchPdes: Boolean; searchBarcodes: Boolean }>("StockSearchFilter").then((previousFilter) => {
			if (previousFilter) {
				dispatch(updateStockField({ key: "searchPdes", value: previousFilter.searchPdes }));
				dispatch(updateStockField({ key: "searchBarcodes", value: previousFilter.searchBarcodes }));
			}
		});
	}, []);

	function saveStockSearchFilter() {
		SaveFilter("StockSearchFilter", { searchPdes: stocks.searchPdes, searchBarcodes: stocks.searchBarcodes });
	}

	useEffect(() => {		
		let rows = 0;
		if (!stocks.selectedRows) {
			setCounterSelectedRows(0);
		}
		if(stocks.selectedRows.length == 0 && serverSideSelectionState.toggledNodes.length > 0){
			gridRef.current?.api.refreshServerSide();
			gridRef.current?.api.deselectAll();
		}
		if (stocks.selectAllFilter) {
			let deselectedRows = 0;
			stocks.deselectedRows.forEach((row: any) => (deselectedRows = deselectedRows + row.length));
			rows = stocks.totalRows - deselectedRows;
		} else if (stocks.selectedRows.some((r) => (r as any).length !== 0)) {
			stocks.selectedRows.forEach((row: any) => (rows = rows + row.length));
		} else {
			rows = stocks.totalRows;
		}

		setCounterSelectedRows(rows);
		const selecteRowsIds: number[] = [];
		stocks.selectedRows.forEach((rowIndex: any) => rowIndex.forEach((row: any) => selecteRowsIds.push(row.id)));
		const deselectedRowsIds: number[] = [];
		stocks.deselectedRows.forEach((rowIndex: any) => rowIndex.forEach((row: any) => deselectedRowsIds.push(row.id)));
	}, [stocks.selectedRows, stocks.deselectedRows, stocks.totalRows]);

	useEffect(() => {
		columnCheckDeselectAll();
	}, [stocks.pageSize]);

	function handleChange(option: any, optionValue: any) {
		dispatch(updateStockField({ key: option, value: optionValue }));
	}

	function handleDeleteStock() {
		setIsDeletingStock(true);	
		dispatch(
			deleteStocks({				
				...gridSearch.current?.filterState,
				ids: stocks.selectAllFilter ? [] : getRowIds(stocks.selectedRows),
				excludedIds: stocks.selectAllFilter ? getRowIds(stocks.deselectedRows) : [],
				searchPdes: gridSearch.current?.searchState?.searchPdes,
				searchBarcodes: gridSearch.current?.searchState?.searchBarcodes,
				keyword: gridSearch.current?.searchState?.keyword,
			})
		).then((res) => {
			if (res.data) {
				columnCheckDeselectAll();
				dispatch(clearFilters());
				gridRef.current?.api.deselectAll();
				dispatch(
					getStockTable({
						...gridSearch.current?.filterState,
					})
				);
				stocks.totalPages - 1 === stocks.pageIndex && handleChange("pageIndex", 0);
			}
			setIsDeletingStock(false);
			setShowDeletePopup(false);
			gridRef.current?.api.refreshServerSide();
		});
		saveStockSearchFilter();
	}

	function selectedRowsUpdate(rows: any[]) {
		let formatedData:{
			positon: number,
			id: number
		} [] = []
		rows.forEach((xe: any) => formatedData.push({ positon: 0, id: xe.id}));
		dispatch(addAGGridSelectedRows([formatedData]));
	}

	function deselectedRowsUpdate(rows: any[]) {		
		let formatedData:{
			positon: number,
			id: number
		} [] = []
		rows.forEach(xe => formatedData.push({ positon: 0, id: xe.id}));
		dispatch(addAGGrideDeselectedRows([formatedData]));
	}

	function columnCheckDeselectAll() {
		handleChange("selectAllFilter", false);
		deselectedRowsUpdate([]);
		selectedRowsUpdate([]);
	}

	function columnCheckSelectAll() {
		handleChange("selectAllFilter", true);
		deselectedRowsUpdate([]);
	}

	function saveSelectedSystemStocks() {
		dispatch(
			importFromProducts({
				filter: {
					...getProductFilterOptions(),
					searchPdes: products.searchPdes,
					searchBarcodes: products.searchBarcodes,
					ids: products.selectAllFilter ? [] : getRowIds(products.selectedRows),
					excludedIds: products.selectAllFilter ? getRowIds(products.deselectedRows) : [],
				},
			})
		).then(() => {
			dispatch(getStockTable({ ...gridSearch.current?.filterState })).then(() => {
				columnCheckDeselectAll();
				setShowSystemStocksPopup(false);
				gridRef.current?.api.refreshServerSide();
			});
		});
	}

	function SetupBulkImport() {
		//Load departments and subdepartments
		dispatch(getDepartments());
		dispatch(getSubDepartments(undefined));
		dispatch(getSuppliers());
		dispatch(getStockGroups());
		dispatch(getManufacturers());

		//Load suppliers
		//Show the modal window!

		handleChange("bulkEditWizardPopup", true);

	}

	function cleanModel(model: ManagedStockView): ManagedStockView {
		//we'll quickly run through the model to remove any invalid Ids (eg subdepartments that don't exist on this store group).
		//This shouldn't occur though, but just incase

		let temp = { ...model };

		if (model.departmentId != null && !departments.table.some((i) => i.id === model.departmentId)) {
			temp.departmentId = null;
		}

		if (model.subdepartmentId != null && !subDepartments.table.some((i) => i.id === model.subdepartmentId && i.departmentId === model.departmentId)) {
			temp.subdepartmentId = null;
		}

		if (model.manufacturerId != null && !manufacturers.table.some((i) => i.id === model.manufacturerId)) {
			temp.manufacturerId = null;
		}

		if (model.supplierId != null && !suppliers.table.some((i) => i.id === model.supplierId)) {
			temp.supplierId = null;
		}

		if (model.stockGroupIds != null) {
			temp.stockGroupIds = model.stockGroupIds.filter((i) => stockGroups.table.some((j) => j.id === i));
		}

		return temp;
	}

	function WholeTableValidation(rows: CommonColumnProperties[]): CommonColumnProperties[] {
		let temp: CommonColumnProperties[] = [...rows];
		let IsValid = (text: any) => text !== undefined && text !== null && text !== "";

		const apnSet = new Set<string>();
		const pdeSet = new Set<string>();

		rows.forEach((row) => {
			if (row.hasError === undefined || row.hasError === false) {
				//don"t bother validating bad rows
				let errors: string[] = [];

				//Duplicate apns and pdes
				if (IsValid(row.__apn)) {
					if (apnSet.has(row.__apn)) {
						errors.push("Duplicate APN");
					} else {
						apnSet.add(row.__apn);
					}
				}

				if (IsValid(row.__pde) && IsValid(row.__supplier)) {
					if (pdeSet.has(row.__pde + "|" + row.__supplier)) {
						errors.push("Duplicate PDE");
					} else {
						pdeSet.add(row.__pde + "|" + row.__supplier);
					}
				}

				//Department
				if (IsValid(row.department)) {
					row.departmentId = departments.table.find((i) => i.name?.toLocaleLowerCase() === row.department?.toLocaleLowerCase())?.id;
					if (row.departmentId === undefined) {
						errors.push("Department not found");
					}
				}
				//Subdepartment
				if (IsValid(row.subdepartment)) {
					if (row.departmentId !== undefined) {
						//Must be set before we try figure out the subdepartment
						row.subDepartmentId = subDepartments.table.find((i) => i.departmentId === row.departmentId && i.name?.toLocaleLowerCase() === row.subdepartment?.toLocaleLowerCase())?.id;
						if (row.subDepartmentId === undefined) {
							errors.push("Subdepartment not found");
						}
					} else {
						errors.push("Department missing for subdepartment matching");
					}
				}

				//Supplier
				if (IsValid(row.supplier)) {
					row.supplierId = suppliers.table.find((i) => i.name?.toLocaleLowerCase() === row.supplier?.toLocaleLowerCase())?.id;
					if (row.supplierId === undefined) {
						errors.push("Supplier not found");
					}
				}
				if (IsValid(row.manufacturer)) {
					row.manufacturerId = manufacturers.table.find((i) => i.name?.toLocaleLowerCase() === row.manufacturer?.toLocaleLowerCase())?.id;
					if (row.manufacturerId === undefined) {
						errors.push("Manufacturer not found");
					}
				}

				if (IsValid(row.stockGroup)) {
					const matchingStockGroup = stockGroups.table.find((i) => i.name?.toLocaleLowerCase() === row.stockGroup?.toLocaleLowerCase());
					if (matchingStockGroup != null) {
						row.stockGroupIds = [matchingStockGroup.id];
					}

					if (row.stockGroupIds === undefined) {
						errors.push("Stock group not found");
					}
				}

				if (IsValid(row.maxStockLevel) && IsValid(row.minStockLevel)) {
					if (Number.parseInt(row.maxStockLevel) < Number.parseInt(row.minStockLevel)) {
						errors.push("Max stock level must be higher than min stock level");
					}
				}
				if (errors.length > 0) {
					row.hasError = true;
					row.validationState = ValidationState.Invalid;
					row.message = [...(row.message == null || row.message == "" ? [] : [row.message]), ...errors].join(", ");
				}
			}
		});
		return temp;
	}

	async function bulkImportUpdateAsync(rows: CommonColumnProperties[]): Promise<any[]> {
		const toImportFromSystem = rows.filter((i) => i.matchedAgainst === "System"); //This is importing from system lines
		const toCreateOrUpdate = rows.filter((i) => i.matchedAgainst === "Managed" || i.matchedAgainst === "None"); //Updating existing managed lines

		setImportPercentage(0);

		const importedFromSystem = await importStockAsync(toImportFromSystem);

		setImportPercentage(33.34);
		const createdOrUpdated = await bulkImportManagedStocks(toCreateOrUpdate);

		return [...importedFromSystem, ...createdOrUpdated];
	}

	async function bulkImportManagedStocks(rows: CommonColumnProperties[]): Promise<any[]> {
		const webWorkerData: StockDetailImportWorkerData = {
			data: rows,
			createCustomStock: createStockFromNewLines,
			updateName: updateStockName,
		};

		const errors: Map<string, string> = new Map<string, string>();

		const { runWorkers, terminateWorkers } = runBatchWorkers<StockDetailImportWorkerData, CommonColumnProperties, StockDetailImportWorkerResponseData>({
			workerData: webWorkerData,
			createWorker: () => new Worker(new URL("./stockDetailsImportWorker.ts", import.meta.url)),
			onWorkerResponse: async (event, responseContext, httpErrors, reject) => {
				if (!responseContext.response.data) {
					if (httpErrors) {
						const combinedError = httpErrors.join(", ");
						// Now using this combined error, construct a { stockName, error } object for each stock and push it onto the errors array
						responseContext.originalBatchData.forEach((r: any) => {
							const errorEntry = errors.get(r.name);
							errors.set(r.name ?? "", errorEntry ? errorEntry + ", " + combinedError : combinedError);
						});
					}
				} else {
					// Add to errors for each item in response.data.nonMatchingStocks in the structure { stockName: s, errorMsg: "Could not find a match for this stock" }
					responseContext.response.data.nonMatchingStocks.forEach((nms) => {
						const errorEntry = errors.get(nms.name);
						errors.set(nms.name, errorEntry ? errorEntry + ", " + "Could not match stock" : "Could not match stock");
					});
				}

				setImportPercentage((prev) => Math.min(50, prev + (1 / responseContext.numBatches) * 16.67));
			},
			onWorkersSuccess: async (workerResponses) => {
				// Only create and update stocks if all workers completed successfully.
				const updateStocks = workerResponses.flatMap((r) => r.data?.stocksToUpdate);
				const createStocks = workerResponses.flatMap((r) => r.data?.stocksToCreate);

				//for all update, just make sure they're all valid
				const updatedStocks = updateStocks.map((i) => cleanModel(i));

				setImportLabel("Creating stocks...");
				setImportPercentage(50);
				const loops = Math.ceil(createStocks.length / batchSize) + Math.ceil(updatedStocks.length / batchSize);
				// TODO: We should move this into web workers to proper batch processing and pooling
				// Dispatch create stock call in batches of 1000
				for (let i = 0; i < createStocks.length; i += batchSize) {
					const createStocksBatch = createStocks.slice(i, Math.min(i + batchSize, createStocks.length));
					const createResponse = await dispatch(createStock(createStocksBatch));
					if (createResponse.errors.length > 0) {
						createResponse.errors.forEach((w) => {
							errors.set(createStocks[w.index].name, w.message);
						});
					}

					setImportPercentage((prev) => Math.min(75, prev + (1 / loops) * 25));
				}

				// TODO: We should move this into web workers to proper batch processing and pooling
				// Dispatch update stock call in batches of 1000
				setImportLabel("Updating stocks...");
				setImportPercentage(75);
				for (let i = 0; i < updatedStocks.length; i += batchSize) {
					const updatedStocksBatch = updatedStocks.slice(i, Math.min(i + batchSize, updatedStocks.length));
					const updateResponse = await dispatch(updateMultipleStocks(updatedStocksBatch));
					if (updateResponse.errors.length > 0) {
						updateResponse.errors.forEach((w) => {
							errors.set(updatedStocks[w.index].name, w.message);
						});
					}
					setImportPercentage((prev) => Math.min(100, prev + (1 / loops) * 25));
				}

				await dispatch(getStockTable({ ...gridSearch.current?.filterState }));
				setImportPercentage(0); //Reset the import percentage
			},
			onWorkersFinished: async (workerResponses, errorData) => {
				// If runWorkers is resolved and we have errors, send all stocks to post-import screen with the error (if applicable)
				const updateStocks = workerResponses.flatMap((r) => r.data?.stocksToUpdate);
				const createStocks = workerResponses.flatMap((r) => r.data?.stocksToCreate);
				const allNames = [...createStocks.map((s) => s.name ?? "No name"), ...updateStocks.map((s) => s.name ?? "No name"), ...errorData.unprocessedData.map((s) => s.name ?? "No name")];

				if (allNames.length > 0) {
					allNames.forEach((name) => {
						const errorEntry = errors.get(name);
						const errorMessage = errorData.error?.message;
						if (errorMessage != null) errors.set(name, errorEntry ? errorEntry + ", " + errorMessage : errorMessage);
					});
				}
			},
		});

		terminateWorkersRef.current = terminateWorkers;
		await runWorkers;

		const returnErrors: { stockName: string; error: string }[] = [];

		errors.forEach((value: string, key: string) => {
			returnErrors.push({
				stockName: key,
				error: value,
			});
		});
		gridRef.current?.api.refreshServerSide();
		return [...returnErrors];
	}

	async function importStockAsync(rows: any[]): Promise<any[]> {
		if (rows.length === 0) return [];

		const webWorkerData: SystemStocksImportWorkerData = {
			data: rows,
		};

		const errors: Map<string, string> = new Map<string, string>();

		const { runWorkers, terminateWorkers } = runBatchWorkers<SystemStocksImportWorkerData, CommonColumnProperties, SystemStocksImportWorkerResponseData>({
			workerData: webWorkerData,
			createWorker: () => new Worker(new URL("./systemStocksImportWorker.ts", import.meta.url)),
			onWorkerResponse: async (event, responseContext, httpErrors, reject) => {
				if (!responseContext.response.data) {
					if (httpErrors) {
						const combinedError = httpErrors.join(", ");
						// Now using this combined error, construct a { stockName, error } object for each stock and push it onto the errors array
						responseContext.originalBatchData.forEach((r: any) => {
							const errorEntry = errors.get(r.name);
							errors.set(r.name ?? "", errorEntry ? errorEntry + ", " + combinedError : combinedError);
						});
					}
				}

				setImportPercentage((prev) => Math.min(16.67, prev + (1 / responseContext.numBatches) * 16.67));
			},
			onWorkersSuccess: async (workerResponses) => {
				const productImports = workerResponses.filter((r) => r.data != null).flatMap((r) => r.data!.productImports);

				setImportPercentage(16.67);
				const loops = Math.ceil(productImports.length / batchSize);
				// TODO: We should move this into web workers to proper batch processing and pooling
				// Dispatch update stock call in batches of 1000
				for (let i = 0; i < productImports.length; i += batchSize) {
					const updatedStocksBatch = productImports.slice(i, Math.min(i + batchSize, productImports.length));
					const importResponse = await dispatch(importFromProductsWithData(updatedStocksBatch));
					if (importResponse.errors.length > 0) {
						importResponse.errors.forEach((w) => {
							errors.set(productImports[w.index].name ?? "No Name", w.message);
						});
					}
					setImportPercentage((prev) => Math.min(33.34, prev + (1 / loops) * 16.67));
				}

				await getStockTable({ ...gridSearch.current?.filterState });
				setImportLabel(undefined);
			},
			onWorkersFinished: (workerResponses, errorData) => {
				// If runWorkers is resolved and we have errors, send all stocks to post-import screen with the error (if applicable)
				const productImports = workerResponses.filter((r) => r.data != null).flatMap((r) => r.data!.productImports);
				const allNames = [...productImports.map((p) => p.name ?? "No name"), ...errorData.unprocessedData.map((d) => d.name ?? "No name")];

				if (allNames.length > 0) {
					allNames.forEach((name) => {
						const errorEntry = errors.get(name);
						const errorMessage = errorData.error?.message;
						if (errorMessage != null) errors.set(name, errorEntry ? errorEntry + ", " + errorMessage : errorMessage);
					});
				}
			},
		});

		terminateWorkersRef.current = terminateWorkers;
		setImportLabel("Importing stocks...");
		await runWorkers;

		const returnErrors: { stockName: string; error: string }[] = [];

		errors.forEach((value: string, key: string) => {
			returnErrors.push({
				stockName: key,
				error: value,
			});
		});

		return [...returnErrors];
	}

	const CustomSearchButton = () => {
		return (
			<>
				<Dropdown.Item onClick={() => handleChange("searchPdes", !stocks.searchPdes)}>
					<Form.Check type="checkbox" label="PDE" checked={stocks.searchPdes} onChange={() => handleChange("searchPdes", !stocks.searchPdes)} onClick={e => e.stopPropagation()}/>
				</Dropdown.Item>
				<Dropdown.Item onClick={() => handleChange("searchBarcodes", !stocks.searchBarcodes)}>
					<Form.Check type="checkbox" label="APN" checked={stocks.searchBarcodes} onChange={() => handleChange("searchBarcodes", !stocks.searchBarcodes)} onClick={e => e.stopPropagation()}/>
				</Dropdown.Item>
			</>
		);
	};

	const additionalOptions: ImportWizardSetting[] = [
		{
			key: "stockGroup",
			element: (
				<ZMultiSelectDropdown
					id="stockGroupsImportOption"
					canClear
					onAddNewOption={async (x) => {
						dispatch(
							createStockGroup({
								name: x,
								id: -1,
								modelId: "",
								locked: false,
							})
						).then((r) => {
							dispatch(getStockGroups());
							setSelectedStockGroupIds((prev) => {
								return [...prev, r.data[0]];
							});
						});
					}}
					maxShownSelectedOptions={2}
					placeholder="Select stock groups"
					label={<ZLabel>Stock Groups</ZLabel>}
					options={stockGroups.table}
					values={stockGroups.table.filter((sg) => selectedStockGroupIds.some((v) => v === sg.id))}
					value={(opt) => opt.id}
					disabled={() => false}
					display={(opt) => opt.name ?? "Unknown"}
					onSelectionChange={(stockGroups) => {
						setSelectedStockGroupIds(stockGroups.map((sg) => sg.id) ?? []);
					}}
					hasSearch
					searchPlaceholder="Type to search or add new stock group"
				/>
			),
		},
		{
			key: "createStockFromNewLines",
			element: (
				<ZCheckboxButton
					label="Create stock from new lines"
					key="createStockFromNewLines"
					type="checkbox"
					checked={createStockFromNewLines}
					onClick={() => setCreateStockFromNewLines(!createStockFromNewLines)}
				/>
			),
		},
		{
			key: "updateStockName",
			element: <ZCheckboxButton label="Update stock name" key="updateStockName" type="checkbox" checked={updateStockName} onClick={() => setUpdateStockName(!updateStockName)} />,
		},
	];

	function onSettingsSubmit() {
		let temp = [...importWizardExtraMatches];

		if (createStockFromNewLines || updateStockName) {
			temp.forEach((col) => {
				if (col.key === "name") {
					col.required = true;
				}
			});
		} else {
			temp.forEach((col) => {
				if (col.key === "name") {
					col.required = false;
				}
			});
		}

		importWizardRef.current?.UpdateColumns(temp);
	}

	function onSettingsReset() {
		setCreateStockFromNewLines(openedFromDropdown);
		setUpdateStockName(false);
		setSelectedStockGroupIds([]);
		setImportPercentage(0);
		setImportLabel(undefined);
	}

	enum StockState {
		Create = 0,
		Update = 1,
		Import = 2,
	}

	function handleStockDetailsImportPostProcess(rows: CommonColumnProperties[]): CommonColumnProperties[] {
		// Add the selected stock group ids from the additional settings page to the stocks that are to be created/updated during import
		const stockIdToRow = new Map<string, number>();

		const replaceFirstErrorMessage = (message: string, replaceWith: string) => {
			if (message.split(", ").length > 1) {
				return [replaceWith, ...message.split(", ").slice(1)].join(", ");
			} else {
				return replaceWith;
			}
		};

		//To make sure we don't try to import the same system stock twice
		let importIds = new Set<number>();

		let isNameValid = (text: any) => text !== undefined && text !== null && text !== "";
		rows.forEach((row, index, arr) => {
			if (row.processed === ProcessingState.Failed && row.result !== "No Match") {
				return row;
			}

			let stockState = StockState.Update;
			if (row.matchedAgainst === "Managed") {
				stockState = StockState.Update;
			} else if (row.matchedAgainst === "System") {
				stockState = StockState.Import;
			} else {
				stockState = StockState.Create;
			}
			// If we are updating, we need to check if there are multiple rows matching to the same stock.
			// If there are, we need to invalidate them
			if (stockState === StockState.Update) {
				// We will create a dictionary here to map stock ids to a row that's matched to it
				// Now, if the entry doesn't exist, we will add it to the map
				// if it does, we will invalidate both the current row and the row that's already in the map
				// With a message saying "Duplicate match to <Stock Name>"
				// If the row doesn't have a stockId, we will skip it
				if (row.id == null) {
					return row;
				}

				if (row.id != "0" && row.id != "" && stockIdToRow.has(row.id)) {
					const existingRowIx = stockIdToRow.get(row.id);
					if (existingRowIx != null) {
						// We always return Matched to <Stock Name> as the first message, so if there's a duplicate match we can replace this message with the new one and keep the other messages
						const existingRowMessage = rows[existingRowIx].message ?? "";
						rows[existingRowIx] = {
							...rows[existingRowIx],
							hasError: true,
							processed: ProcessingState.Failed,
							message: replaceFirstErrorMessage(existingRowMessage, `Duplicate match to ${row.__name}`),
						};

						row.hasError = true;
						row.message = replaceFirstErrorMessage(row.message ?? "", `Duplicate match to ${row.__name}`);
						row.processed = ProcessingState.Failed;
					}
				} else {
					stockIdToRow.set(row.id, index);
				}
			} else {
				// Matched against System, if the setting to create stock from new lines isn't enabled, this row is invalid
				if (!createStockFromNewLines) {
					row.processed = ProcessingState.Failed;
					row.hasError = true;
					row.message = `${row.matchedAgainst === "System" ? "System" : "No"} Match - Create lines option not enabled`;
				}
			}

			const rowIdNumber = parseInt(row.id ?? "");

			if (stockState === StockState.Import && !row.hasError && Number.isInteger(rowIdNumber) && importIds.has(rowIdNumber)) {
				row.processed = ProcessingState.Failed;
				row.hasError = true;
				row.message = "Duplicate master product - trying to import the same line twice";
			} else if (Number.isInteger(rowIdNumber)) {
				importIds.add(rowIdNumber);
			}

			row = {
				...row,
				stockGroupIds: [...(row.stockGroupIds != null ? row.stockGroupIds : []), ...selectedStockGroupIds].filter(onlyUnique),
				stockState: stockState,
			};

			rows[index] = { ...row };

			// We only want to do this post-processing if we are creating stock from new (aka non-matching) lines.
			if (createStockFromNewLines) {
				let newRow = { ...row };
				if (row.matchedAgainst === "None") {
					// For each non-matched stock, we need to do a few things:
					// 1. Validate that name is not empty => If it is, skip it (it's already failed).
					if (!isNameValid(newRow.name)) {
						newRow.hasError = true;
						let errors = ["Name is required for new custom stocks"];

						if (newRow.message) {
							errors = [newRow.message, ...errors];
						}

						newRow.message = errors.join(",");
						rows[index] = { ...newRow };
					}

					if (newRow.costEx == null || newRow.costEx == "") {
						newRow.costEx = 0;
					}

					// 2. If the row is missing required fields such as min order qty, set a default value here.
					newRow = {
						minOrderQty: 1,
						cartonSize: 1,
						minStockLevel: 1,
						active: true,
						ethical: false,
						gstCode: "N",
						...newRow,
						costEx: isNaN(parseFloat(newRow.costEx)) ? 0 : parseFloat(newRow.costEx),
						baseSellPrice: isNaN(parseFloat(newRow.baseSellPrice)) ? 0 : parseFloat(newRow.baseSellPrice),
						// 3. Set ProcessingState to Done
						message: "",
						processed: ProcessingState.Done,
						stockState: StockState.Create,
					};
				}

				rows[index] = { ...newRow };
				// This can then get forwarded to the import stage where it will be checked.
			}
		});

		return rows;
	}	

	const gridRef = useRef<AgGridReact>(null);

	const getServerSideDatasource: () => IServerSideDatasource = () => {
		return {	
			getRows: async (params) => {
				if (gridSearch.current.isExcelExport == true){
					setIsExportingExcel(true);
				}
				else if(gridSearch.current.isCsvExport == true){
					setIsExportingCsv(true);
				}

				const respopnse = await dispatch(
					getStockTableOptimised({
						keyword: gridSearch.current?.searchState?.keyword,
						contains: true,
						searchPdes: gridSearch.current?.searchState?.searchPdes,
						searchBarcodes: gridSearch.current?.searchState?.searchBarcodes,
						offset: gridSearch.current == null || gridSearch.current?.isCsvExport != true ||
								 gridSearch.current?.isExcelExport != true? params.request.startRow: null,
						limit: params.request.endRow != undefined && params.request.startRow != undefined ? params.request.endRow - params.request.startRow : 100,
						sortModels: params.request.sortModel,
						loadDetails:  [ManagedStockSearchFilterQueryLoadDetails.Pdes, ManagedStockSearchFilterQueryLoadDetails.Apns],
						...gridSearch.current?.filterState,
					})
				);				
				
				setTotalRows(respopnse.totalRows);

				handleChange("keyword", gridSearch.current?.searchState?.keyword);
				handleChange("searchPdes", gridSearch.current?.searchState?.searchPdes);
				handleChange("searchBarcodes", gridSearch.current?.searchState?.searchBarcodes);

				if (respopnse.totalRows == 0){
					params.api.showNoRowsOverlay();
				} else {
					params.api.hideOverlay();
				}

				params.success({
					rowData: respopnse.data,
					rowCount: respopnse.totalRows,
				});
			},
		};
	};

	const onGridReady = useCallback((params: GridReadyEvent) => {
		params.api!.setGridOption("serverSideDatasource", getServerSideDatasource());		
	}, []);

	function canMerge(): boolean{		
		const ids = getRowIds(stocks.selectedRows);
		const selectedSystemStocks = stocks.table.filter(s => ids.find(id => id === s.id) != null && s.productNo != null);
		return !(selectedSystemStocks.length > 1 || ids.length < 2);
	}

	function setSearchStateAndFilters(existingSearchState: SearchState | undefined, filter: FilterState | undefined){
		dispatch(cleanStockDetails());
		let searchParams : SearchState = {};
		if(existingSearchState == null || existingSearchState == undefined){
			gridSearch.current = {searchState: existingSearchState, filterState: filter};
		} else {
			searchParams = { searchBarcodes: stocks.searchBarcodes,  searchPdes: stocks.searchPdes, keyword: existingSearchState.keyword, contains: existingSearchState.contains }
			gridSearch.current = {searchState: searchParams, filterState: filter};
		}
		gridRef.current?.api.refreshServerSide()
	}

	function exportCsv(){
		return new Promise<void> ((resolve, reject) => {	
				gridRef.current?.api.exportDataAsCsv(getManagerStockCsvDataFormatter());
			resolve();			
		});		
	}

	function exportExcel(){
		return new Promise<void> ((resolve, reject) => {	
				let filters="";
				if(gridSearch.current?.searchState?.keyword != null)
					filters = "Search: "+ gridSearch.current?.searchState?.keyword+ ", \r\n";
				if(gridSearch.current?.searchState?.searchBarcodes == true)
					filters += "Searching APN'S: true\r\n";
				if(gridSearch.current?.searchState?.searchPdes == true)
					filters += "Searching PDE'S: true\r\n";

				if(gridSearch.current?.filterState?.supplierIds != null)
					filters += `Suppliers: ${suppliers.table.find(x=> x.id == gridSearch.current?.filterState?.supplierIds[0])?.name} \r\n`;
				if(gridSearch.current?.filterState?.departmentIds != null)
					filters += `Department: ${departments.table.find(x=> x.id == gridSearch.current?.filterState?.departmentIds[0])?.name} \r\n`;
				if(gridSearch.current?.filterState?.subdepartmentIds != null)
					filters += `Sub Department: ${subDepartments.table.find(x=> x.id == gridSearch.current?.filterState?.subdepartmentIds[0])?.name} \r\n`;
				if(gridSearch.current?.filterState?.manufacturerIds != null)
					filters += `Manufacturer: ${manufacturers.table.find(x=> x.id == gridSearch.current?.filterState?.manufacturerIds[0])?.name} \r\n`;				
				if(gridSearch.current?.filterState?.stockGroupIds != null)
					filters += `Stock Group: ${stockGroups.table.find(x=> x.id == gridSearch.current?.filterState?.stockGroupIds[0])?.name} \r\n`;

				if(gridSearch.current?.filterState?.active != null)
					filters += `Active: ${gridSearch.current?.filterState?.ethical == true? "Yes": "No"} \r\n`;
				if(gridSearch.current?.filterState?.ethical != null)
					filters += `Ethical: ${gridSearch.current?.filterState?.ethical == true? "Yes": "No"} \r\n`;
				if(gridSearch.current?.filterState?.schedule != null && gridSearch.current?.filterState?.schedule != "")
					filters += `Schedule: ${gridSearch.current?.filterState?.schedule} \r\n`;
				if(gridSearch.current?.filterState?.pbs != null)
					filters += `Pbs: ${gridSearch.current?.filterState?.pbs == true? "Yes": "No"} \r\n`;




				const spreadSheets = [];
				spreadSheets.push(gridRef.current?.api.getSheetDataForExcel(getManagedStockCoverPage(filters)));
				spreadSheets.push(gridRef.current!.api.getSheetDataForExcel(getManagerStockDataFormatter()));
				gridRef.current!.api.exportMultipleSheetsAsExcel({
					data: spreadSheets,
					fileName: "ManagedStock_" + new Date().toLocaleDateString('en-AU', {
						day : 'numeric',
						month : 'short',
						year : 'numeric',
						hour : '2-digit',
						minute : '2-digit'
					}).split(' ').join('-').replace(',', '') +".xlsx"
				});
			
			resolve();
		});
	}

	function refreshSupportData() {		

		if(filterRequiresRefresh==true || departments.table.length == 0)
			dispatch(getDepartments());

		if(filterRequiresRefresh==true ||subDepartments.table.length == 0)
			dispatch(getSubDepartments(undefined));

		if(filterRequiresRefresh==true ||suppliers.table.length == 0)
			dispatch(getSuppliers());

		if(filterRequiresRefresh==true ||stockGroups.table.length == 0)
			dispatch(getStockGroups());
		
		if(filterRequiresRefresh==true ||manufacturers.table.length == 0)
			dispatch(getManufacturers());
		setFilterRequiresRefresh(false);
	}

	return (
		<>
			<Title
				pageTitle="Managed Stocks"
				appendTitle={
					global.numStockConflicts > 0 ? (
						<ZFeatureFlag flag="ZFreedom.ManagedStock.StockConflicts" value={true}>
							<Z.Tooltip text={`You have ${global.numStockConflicts} stock conflicts, click here to resolve them`}>
								<Z.Button
									variant={`${global.numStockConflicts > 20 ? "danger" : "warning"}`}
									className="cta-button py-0 px-1"
									icon={ZIconType.Alert}
									onClick={() => navigate("/app/stock-conflicts")}
									buttonText={`${global.numStockConflicts < 100 ? global.numStockConflicts : "99+"}`}
								/>
							</Z.Tooltip>
						</ZFeatureFlag>
					) : (
						<></>
					)
				}
			/>

			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							 <AgGridToolbar 
							    search={true}
								ref={toolbarRef}
								className="pb-1"
								handleSubmit={(searchState, filterState) => {
									setSearchStateAndFilters(searchState, filterState);
								}}
								handleClearFilters={() => {
									setSearchStateAndFilters(gridSearch.current?.searchState, undefined);
								}}
								handleFilterOpen={()=> refreshSupportData()}
								filters={[
									createSingleSelectFilter<ManagedSupplier>({
										label: "Supplier",
										filterName: "supplierIds",
										options: suppliers.table,
										value: (option) => [option.id],
										display: (option) => option.name,
										hasNoneOption: true,
									}),
									createSingleSelectFilter<ManagedDepartment>({
										label: "Department",
										filterName: "departmentIds",
										options: departments.table,
										value: (option) => [option.id],
										display: (option) => option.name,
										onFilterChange: (e) => setSelectedDepartment(e),
										hasNoneOption: true,
									}),
									createSingleSelectFilter<ManagedSubdepartment>({
										label: "Sub Department",
										filterName: "subdepartmentIds",
										options: subDepartments.table.filter( i=> i.departmentId === selectedDepartment?.id),
										value: (option) => [option.id],
										display: (option) => option.name,
										hasNoneOption: true,
									}),
									createSingleSelectFilter<ManagedManufacturer>({
										label: "Manufacturer",
										filterName: "manufacturerIds",
										options: manufacturers.table,
										value: (option) => [option.id],
										display: (option) => option.name,
										hasNoneOption: true,
									}),
									createSingleSelectFilter<ManagedStockGroup>({
										label: "Stock Group",
										filterName: "stockGroupIds",
										options: stockGroups.table,
										value: (option) => [option.id],
										display: (option) => option.name,
										hasNoneOption: true,
									}),
									createBooleanSelectFilter({
										label: "Active",
										filterName: "active",
										hasNoneOption: true,
									}),
									createBooleanSelectFilter({
										label: "Ethical",
										filterName: "ethical",
										hasNoneOption: true,
									}),
									createSingleSelectFilter<string>({
										label: "Schedule",
										filterName: "schedule",
										options: ScheduleCodes,
										value: (option) => option,
										display: (option) => option,
										hasNoneOption: true,
									}),
									createBooleanSelectFilter({
										label: "PBS",
										filterName: "pbs",
										hasNoneOption: true,
									}),
								]}
								searchProps={
									{totalRows: stocks.totalRows, placeholder: "by Name" + (stocks.searchPdes == true ?" / PDE":"" ) + (stocks.searchBarcodes == true? " / APN" : ""), customSearch: CustomSearchButton(), hideContains: true}
								}
								children={(<div className="d-inline-flex gap-1">
									{ 
									(gridRef.current?.api && serverSideSelectionState.toggledNodes.length >= 1 && stocks.selectAllFilter != true) &&									
										<Z.Button variant="light" className="h-100" disabled={true} buttonText={serverSideSelectionState.toggledNodes.length+ " Selected"}/>
									}
									{
									(stocks.selectAllFilter == true && stocks.deselectedRows.length == 0) &&
										<Z.Button variant="light" className="h-100" disabled={true} buttonText={stocks.totalRows + " Selected"}/>									
									}
									{
									(stocks.selectAllFilter == true && stocks.deselectedRows.length > 0) &&
										<Z.Button variant="light" className="h-100" disabled={true} buttonText={stocks.totalRows - stocks.deselectedRows[0].length + " Selected"}/>
									}
									
										<Dropdown>
											<Dropdown.Toggle variant="light" id="dropdown-basic" className="btn-icon custom-btn-icon h-100">
												<ZIcon icon={ZIconType.Add} size="18px" />
											</Dropdown.Toggle>
											<Dropdown.Menu className="w-auto">
												<Dropdown.Item
													className="d-inline-flex align-items-center gap-1"
													onClick={
														user.permissions.managedStock.canCreate
															? () => {
																	setShowSystemStocksPopup(true);																	
																}
															: undefined
													}
												>
													<ZIcon icon={ZIconType.System} size="18px" /> Import System Stock
												</Dropdown.Item>
												<Dropdown.Item
													className="d-inline-flex align-items-center gap-1"
													onClick={() => {
														navigate("/app/stock/new");														
													}}
												>
													<ZIcon icon={ZIconType.Add} size="18px" /> Create Custom Stock
												</Dropdown.Item>
												<Dropdown.Item
													className="d-inline-flex align-items-center gap-1"
													onClick={
														user.permissions.managedStock.canUpdate
															? () => {
																	dispatch(getDepartments());
																	dispatch(getSubDepartments(undefined));
																	dispatch(getSuppliers());
																	setOpenedFromDropdown(true);
																	SetupBulkImport();
																}
															: undefined
													}
												>
													<ZIcon icon={ZIconType.Upload} size="18px" /> Import Spreadsheet
												</Dropdown.Item>
											</Dropdown.Menu>
										</Dropdown>
									<ZTooltip text={
											serverSideSelectionState.toggledNodes.length === 1 ? (
												"Edit" 
											) :
											(serverSideSelectionState.toggledNodes.length > 1 ?(
												`Edit ${serverSideSelectionState.toggledNodes.length} rows`
											) :
											(serverSideSelectionState.selectAll && stocks.deselectedRows.length == 0? (`Edit all rows`) : "Edit" ))
										}>
										<Z.Button icon={ZIconType.Edit} buttonSize="md" onClick={() => {
											if (gridRef.current?.api && serverSideSelectionState.toggledNodes.length > 1 || serverSideSelectionState.selectAll == true) {
												handleChange("bulkEditPopup", true)
											} else {
												navigate(`/app/stock/${serverSideSelectionState.toggledNodes[0].id}`);
											}
										} } disabled={!user.permissions.managedStock.canUpdate || serverSideSelectionState.toggledNodes.length === 0 && serverSideSelectionState.selectAll != true} />
									</ZTooltip>
									<ZTooltip text={
										(serverSideSelectionState.toggledNodes.length > 1 || serverSideSelectionState.selectAll) ? 
											(serverSideSelectionState.toggledNodes.length > 10 ? 
												"Cannot merge more than 10 lines at a time" : (!canMerge() && serverSideSelectionState.toggledNodes.length > 1) ? "Cannot merge multiple system lines" : "Merge"
											) 
										: "Please select 2 or more items to merge"
										}>
										<Z.Button icon={ZIconType.Merge} buttonSize="md" onClick={() => handleChange("manualMergePopup", true)}											
											disabled={!canMerge() || serverSideSelectionState.toggledNodes.length > 10 || serverSideSelectionState.toggledNodes.length == 0} />
									</ZTooltip>
									<ZTooltip text={
										serverSideSelectionState.toggledNodes.length == 0 && serverSideSelectionState.selectAll != true? `Please select a row to delete`:										  
										serverSideSelectionState.toggledNodes.length > 0 ? `Delete ${serverSideSelectionState.toggledNodes.length} rows` :
										serverSideSelectionState.toggledNodes.length > 0 || serverSideSelectionState.selectAll ? `Delete all rows`:
										 "Please select a row to delete"}>
										<Z.Button icon={ZIconType.Delete} buttonSize="md"  onClick={() => (user.permissions.managedStock.canDelete ? setShowDeletePopup(true) : undefined)} 
											disabled={!user.permissions.managedStock.canDelete || serverSideSelectionState.toggledNodes.length < 1  && serverSideSelectionState.selectAll != true} />
									</ZTooltip>
									<ZTooltip text="Refresh">
										<Z.Button icon={ZIconType.Refresh} buttonSize="md" onClick={() => {
											gridRef.current?.api.refreshServerSide();
											setFilterRequiresRefresh(true);
										}} 
											disabled={!user.permissions.managedStock.canView} />
									</ZTooltip>
									<ZTooltip text="Sync">
										<Z.Button icon={ZIconType.Sync} buttonSize="md" onClick={() => setSyncOpen(true)} />
									</ZTooltip>
									<ZTooltip text="Import stock details">
										<ZButton
											size="sm"
											className="btn-icon custom-btn-icon"
											onClick={() => {
												setOpenedFromDropdown(false);
												SetupBulkImport();
											}}
											disabled={!user.permissions.managedStock.canCreate || !user.permissions.managedStock.canUpdate} 
											icon={ZIconType.Upload}
											iconSize="18px"
											style={{height: 40.4}}
									/>
									</ZTooltip>
									<Dropdown>
										<ZTooltip text="Export">
											<Dropdown.Toggle variant="light" id="dropdown-basic" className="btn-icon custom-btn-icon" style={{minHeight: 40.4}}
												disabled={isExportingCsv || isExportingExcel}
											>
												<ZIcon icon={ZIconType.Download} size="18px"/>
											</Dropdown.Toggle>
										</ZTooltip>
										<Dropdown.Menu className="w-auto">
											<Dropdown.Item className="d-inline-flex align-items-center gap-1" 
												onClick={() => {									
													let loadedRows = 0;
													gridRef.current?.api.forEachNode(node => {
														loadedRows++;
													});											
													if (totalRows == loadedRows){
														setIsExportingCsv(true);
														exportCsv().then(()=>{
															setTimeout(() => { 
																setIsExportingCsv(false);
															},400);
														});
														return;
													}
													gridSearch.current.isCsvExport = true;	
													setCacheBlockSize(totalRows);
												}}>
												<ZIcon icon={ZIconType.File} /> CSV File</Dropdown.Item>
											<Dropdown.Item className="d-inline-flex align-items-center gap-1" onClick={() => 
												{											
													let loadedRows = 0;
													gridRef.current?.api.forEachNode(node => {
														loadedRows++;
													});													
													if (totalRows == loadedRows){
														setIsExportingExcel(true);
														exportExcel().then(()=>{
															setTimeout(() => { 
																setIsExportingExcel(false);
															},400);
														});
														return;
													}
													gridSearch.current.isExcelExport = true;
													setCacheBlockSize(totalRows);
												}
											}
											 ><ZIcon icon={ZIconType.Excel} /> Excel File</Dropdown.Item>									
										</Dropdown.Menu>
									</Dropdown>
								</div>
									
								)}
							/>							
								
							<ManagedStockMerge
								loadStocks={false} //TODO: We can remove this property once we tear redux out of the managed stock page
								show={stocks.manualMergePopup}
								stockIds={getRowIds(stocks.selectedRows)}
								handleClose={() => handleChange("manualMergePopup", false)}
								afterMerge={(mergedStockId: number) => {
									handleChange("manualMergePopup", false);
									navigate(`/app/stock/${mergedStockId}?openSync=true`);
								}}
							/>
							<SystemStocksPopup
								show={showSystemStocksPopup}
								onClose={() => {
									setShowSystemStocksPopup(false);
									gridRef.current?.api.refreshServerSide();
								}}
								onSave={saveSelectedSystemStocks}
								canSelectAll={true}
							/>
							<ManagedStockSync
								isOpen={syncOpen}
								onClose={() => setSyncOpen(false)}
								counter={counterSelectedRows}
								filter={{
									keyword: gridSearch.current?.searchState?.keyword,
									contains: true,
									...gridSearch.current?.filterState,
									ids: stocks.selectAllFilter ? [] : getRowIds(stocks.selectedRows),
									excludedIds: stocks.selectAllFilter ? getRowIds(stocks.deselectedRows) : [],
								}}
							/>
							<BulkAgGridEdit 
								show={stocks.bulkEditPopup}
								handleClose={() => {
									handleChange("bulkEditPopup", false);
									columnCheckDeselectAll();
									gridRef.current?.api.deselectAll();
									gridRef.current?.api.refreshServerSide();
								} } 
								filter={{
									ids: (stocks.selectAllFilter ? [] : getRowIds(stocks.selectedRows)),
									excludedIds: (stocks.selectAllFilter ? getRowIds(stocks.deselectedRows) : []),
									keyword: gridSearch.current?.searchState?.keyword,
									contains: true,
									...gridSearch.current?.filterState
								}}
							/>

							{/* Bulk Import/Update Wizard */}
							<ImportWizard
								ref={importWizardRef}
								title="Stock Details"
								display={stocks.bulkEditWizardPopup}
								handleClose={() => {
									if (terminateWorkersRef.current) terminateWorkersRef.current();
									handleChange("bulkEditWizardPopup", false);
								}}
								importPercentage={importPercentage}
								importLabel={importLabel}
								importAsync={(i) => bulkImportUpdateAsync(i)}
								postProcessStep={(r) => handleStockDetailsImportPostProcess(r)}
								customColumns={importWizardExtraMatches}
								validateTable={(rows) => WholeTableValidation(rows)}
								matchAgainst="Both"
								finish={() => handleChange("bulkEditWizardPopup", false)}
								onSettingsSubmit={onSettingsSubmit}
								onSettingsReset={onSettingsReset}
								showAllImportByColumns
								matchedColumnOverride={{
									headerName: "Status",
									pinned: true,
									sortable: true,
									field: "stockState",
									maxWidth: 500,
									cellRendererSelector: (params) => {
										const issueArray = params.data.message.split(", ");
										const cellRendererParams = {
											decorator:
												params.data.processed === ProcessingState.Failed ? (
													<ZTooltip
														tooltipClassName="tooltip-text-start"
														text={`This stock has issues${issueArray.length > 0 ? ": \n• " + issueArray.join("\n• ") : ""}`}
													>
														<ZIcon iconClassName="me-1" icon={ZIconType.Alert} variant="warning" />
													</ZTooltip>
												) : params.value === StockState.Create ? (
													<ZTooltip text="This stock will be created">
														<ZIcon icon={ZIconType.Add} variant="primary" />
													</ZTooltip>
												) : params.value === StockState.Import ? (
													<ZTooltip text="This stock will be imported">
														<ZIcon icon={ZIconType.Upload} variant="primary" />
													</ZTooltip>
												) : (
													<ZTooltip text="This stock will be updated">
														<ZIcon icon={ZIconType.Check} variant="primary" />
													</ZTooltip>
												),
											decoratorPosition: "start",
											align: "left",
											children: (
												<>{params.data.processed === ProcessingState.Failed ? <div className="text-truncate overflow-hidden">{params.data.message}</div> : <></>}</>
											),
										};
										return { component: "baseCellRenderer", params: cellRendererParams };
									},
								}}
								additionalSettings={additionalOptions}
							/>
					
							<AgGridReact<ManagedStockOptimizedView>
								ref={gridRef}
								columnDefs={getManagedStockViewConfig(user, departments, suppliers)}
								rowModelType="serverSide"
								pagination={true}
								paginationPageSize={20}
								getRowId={(p) => p.data.id.toString()}
								cacheBlockSize={cacheBlockSize}
								onGridReady={onGridReady}
								containerStyle={{ height: "75dvh" }}
								className="ag-theme-quartz ag-theme-custom w-100"
								suppressContextMenu
								rowSelection="multiple"
								suppressRowClickSelection={true}
								suppressCellFocus={true}																	
								excelStyles={
									[
										{
											id: 'header',
											font: {
												bold: true
											}
										},
										{
											id:'numberAsString',
											dataType: 'String'
										},
										{
											id:'moneyField',
											numberFormat: {
												format: '$#,##0.00',
											}
										},		
										{
											id: 'wrapText',
											alignment: {
												wrapText: true,
												vertical: "Top",
												shrinkToFit: true								
											},
											
										},
										{
											id: 'top',
											alignment: {
												vertical: "Top",
											},
										},
										{
											id: 'coverPageMainHeader',
											alignment: {
												vertical: "Top",
												horizontal: "Right",
											},
											font: {
												bold: true,
												size: 14
											}
										},
										{
											id: 'coverPageHeading',
											alignment: {
												vertical: "Top",
												horizontal: "Right",
											},
											font: {
												bold: true,
											}
										},
									]}
								onSelectionChanged={(e) => {
									const selection = gridRef.current?.api.getServerSideSelectionState();
									const nodes = selection?.toggledNodes?.map(t => gridRef.current?.api.getRowNode(t.toString())?.data);
									if(stocks.selectAllFilter == true){
										const matchingData = stocks.table.filter(x=> nodes?.find(e=>e.id == x.id));											
										deselectedRowsUpdate(matchingData);												
									} else {
										//@ts-ignore
										setServerSideSelectionState(selection && nodes ? {selectAll: selection.selectAll, toggledNodes: nodes} : {selectAll: false, toggledNodes: []});
										selectedRowsUpdate(nodes!);
									}
								}}
								onRowSelected={(event: RowSelectedEvent<any, any>) => {
									if(event.source != "uiSelectAll" && stocks.selectAllFilter == (event.api.getServerSideSelectionState() as IServerSideSelectionState).selectAll){
										return;
									}											
									if((event.api.getServerSideSelectionState() as IServerSideSelectionState).selectAll){
										columnCheckSelectAll();
									} else {
										columnCheckDeselectAll();
									}
								}}
								components={{
									baseCellRenderer: BaseCellRenderer,
									priceCellRenderer: PriceCellRenderer, 
									percentCellRenderer: PercentCellRenderer,
									dateCellRenderer: DateCellRenderer,									
								}}
								onModelUpdated={()=>{
									if(isExportingExcel == false && isExportingCsv == false){
										return;
									}
									
									gridSearch.current.isCsvExport = false;
									gridSearch.current.isExcelExport = false;

									if(isExportingExcel == true){										
										exportExcel().then(()=>{
											setTimeout(() => { 
												setIsExportingExcel(false);
												setCacheBlockSize(100);
											},400);								
										});										
									} else {
										exportCsv().then(()=>{
											setTimeout(() => { 
												setIsExportingCsv(false);
												setCacheBlockSize(100);
											},400);
										});
									}
								}}
							/>
						</Card.Body>
					</Card>
				</Col>
			</Row>
			<ManagedStocksDeletePopup
				isDeleting = {isDeletingStock}
				showPopup = {showDeletePopup}
				selectedRowsCount = {serverSideSelectionState.selectAll? 4: serverSideSelectionState.toggledNodes.length}
				closeDeletePopup = {()=>setShowDeletePopup(false)}
				ActionDelete = {() => (user.permissions.managedStock.canDelete ? handleDeleteStock() : undefined)}
			/>
			<BSModal
				centered
				size={"sm"}
				show={isExportingExcel == true || isExportingCsv == true}
				keyboard={false}
				animation={true}
				backdrop={"static"}
				
			>
				<BSModal.Dialog className="m-0">
					<BSModal.Body>
						<div style={{textAlign: 'center'}}>Exporting</div>
					</BSModal.Body>
				</BSModal.Dialog>
			</BSModal>
		</>
	);
}